import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft,
  Settings,
  Eye,
  Palette,
  Monitor,
  Layout,
  Play,
  RefreshCw
} from 'lucide-react'
import { 
  MomentSettings, 
  defaultSettings, 
  saveMomentSettings, 
  loadMomentSettings, 
  generateMomentSDKCode,
  generateMomentSDKConfig
} from '../utils/momentSettings'

const MomentCustomizationPage: React.FC = () => {
  // Load settings from localStorage on component mount
  const [config, setConfig] = useState<MomentSettings>(() => loadMomentSettings())
  const [activeTab, setActiveTab] = useState('basic')
  const [showCode, setShowCode] = useState(false)

  // Auto-save settings whenever config changes
  useEffect(() => {
    saveMomentSettings(config)
  }, [config])

  // Function to update config and trigger auto-save
  const updateConfig = (updates: Partial<MomentSettings>) => {
    setConfig(prev => ({ ...prev, ...updates }))
  }

  // Function to update nested config properties
  const updateNestedConfig = (path: string, value: any) => {
    setConfig(prev => {
      const newConfig = { ...prev }
      const keys = path.split('.')
      let current: any = newConfig
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      current[keys[keys.length - 1]] = value
      
      return newConfig
    })
  }

  const initializePreview = async () => {
    try {
      // Initialize MomentScience SDK with current settings
      const sdkConfig = generateMomentSDKConfig(config)
      
      console.log('🚀 Starting MomentScience preview...')
      console.log('📋 SDK Config:', sdkConfig)
      
      // Configure window object
      ;(window as any).AdpxConfig = sdkConfig
      ;(window as any).AdpxUser = {
        placement: 'customization_preview',
        theme_id: 'moment_customization_preview',
        country: 'US',
        // Add some test values to help with targeting
        order_value: 199.99,
        currency: 'USD',
        user_id: 'preview_user_' + Date.now()
      }
      
      console.log('👤 User Config:', (window as any).AdpxUser)

      // Ensure embedded container exists if embedded mode is enabled
      if (sdkConfig.settings.embedded.enabled) {
        const targetElement = document.getElementById(sdkConfig.settings.embedded.targetElement)
        if (!targetElement) {
          console.warn(`⚠️ Embedded target element #${sdkConfig.settings.embedded.targetElement} not found!`)
        } else {
          console.log(`🎯 Found embedded target element: #${sdkConfig.settings.embedded.targetElement}`)
          // Clear any existing content
          targetElement.innerHTML = ''
        }
      }

      // Clean up any existing SDK instances
      if ((window as any).Adpx) {
        console.log('🔄 Existing SDK found, reinitializing...')
        try {
          // Try to destroy existing instance if method exists
          if (typeof (window as any).Adpx.destroy === 'function') {
            ;(window as any).Adpx.destroy()
          }
        } catch (e) {
          console.log('⚠️ Could not destroy existing SDK instance:', e)
        }
      }

      // Remove existing script if present
      const existingScript = document.getElementById('adpx-launcher-preview')
      if (existingScript) {
        console.log('🗑️ Removing existing SDK script...')
        existingScript.remove()
        // Clear the Adpx object to force reload
        delete (window as any).Adpx
      }

      // Load SDK script
      console.log('📦 Loading MomentScience SDK...')
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = 'https://cdn.pubtailer.com/launcher.min.js'
      script.crossOrigin = 'anonymous'
      script.async = true
      script.id = 'adpx-launcher-preview'
      
      // Wait for script to load
      await new Promise((resolve, reject) => {
        script.onload = () => {
          console.log('✅ SDK script loaded successfully')
          resolve(null)
        }
        script.onerror = (error) => {
          console.error('❌ Failed to load SDK script:', error)
          reject(error)
        }
        document.head.appendChild(script)
      })

      // Wait a moment for SDK to initialize
      await new Promise(resolve => setTimeout(resolve, 500))

      // Initialize SDK
      if ((window as any).Adpx) {
        console.log('🎯 Initializing SDK with config...')
        ;(window as any).Adpx.init(sdkConfig)
        
        // Wait a bit more and check if offers are loaded
        setTimeout(() => {
          console.log('🔍 Checking for loaded offers...')
          if ((window as any).Adpx && typeof (window as any).Adpx.getOffers === 'function') {
            const offers = (window as any).Adpx.getOffers()
            console.log('📊 Available offers:', offers)
            if (!offers || offers.length === 0) {
              console.log('⚠️ No offers available. This could be due to:')
              console.log('  • Account configuration')
              console.log('  • Targeting rules')
              console.log('  • Geographic restrictions')
              console.log('  • Theme/placement settings')
            }
          }
          
          // Force show if autoShow is enabled
          if (sdkConfig.autoShow && (window as any).Adpx && typeof (window as any).Adpx.show === 'function') {
            console.log('🎪 Forcing show with autoShow...')
            ;(window as any).Adpx.show()
          }
        }, 1000)
        
        console.log('🎉 MomentScience preview initialized successfully!')
      } else {
        console.error('❌ SDK not available after loading script')
        alert('Failed to load MomentScience SDK. Please check your internet connection and try again.')
      }
      
    } catch (error) {
      console.error('💥 Error initializing preview:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      alert(`Preview failed: ${errorMessage}. Check console for details.`)
    }
  }

  const resetToDefaults = () => {
    setConfig(defaultSettings)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  }

  const handleToggle = (key: string, section?: string) => {
    if (section === 'embedded') {
      updateNestedConfig(`embedded.${key}`, !config.embedded[key as keyof typeof config.embedded])
    } else {
      updateConfig({ [key]: !config[key as keyof MomentSettings] } as Partial<MomentSettings>)
    }
  }

  const handleInputChange = (key: string, value: string | number, section?: string) => {
    if (section === 'embedded') {
      updateNestedConfig(`embedded.${key}`, value)
    } else {
      updateConfig({ [key]: value } as Partial<MomentSettings>)
    }
  }

  const handleStyleChange = (section: string, key: string, value: string | any) => {
    updateNestedConfig(`styles.${section}.${key}`, value)
  }

  const generateCode = () => {
    return generateMomentSDKCode(config)
  }

  const positions = [
    { value: 'center', label: 'Center' },
    { value: 'bottom-center', label: 'Bottom Center' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'bottom-right', label: 'Bottom Right' },
    { value: 'top-center', label: 'Top Center' },
    { value: 'top-left', label: 'Top Left' },
    { value: 'top-right', label: 'Top Right' }
  ]

  const animations = [
    { value: '', label: 'None' },
    { value: 'top-bottom', label: 'Top to Bottom' },
    { value: 'bottom-top', label: 'Bottom to Top' },
    { value: 'left-right', label: 'Left to Right' },
    { value: 'right-left', label: 'Right to Left' }
  ]

  const ToggleButton = ({ isOn, onToggle, label }: { isOn: boolean, onToggle: () => void, label: string }) => (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-[#1A2B49]">{label}</span>
      <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#FE4215] focus:ring-offset-2 ${
          isOn ? 'bg-[#FE4215]' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            isOn ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Navigation */}
      <nav className="p-6 bg-white shadow-sm">
        <div className="container mx-auto flex items-center justify-between">
          <Link
            to="/moment-perks"
            className="inline-flex items-center space-x-2 text-[#6E6D7A] hover:text-[#3B82F6] transition-colors duration-200 font-sans"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to MomentPerks</span>
          </Link>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Settings className="w-6 h-6 text-[#FE4215]" />
              <span className="text-[#1A2B49] font-medium font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                MomentScience Customization
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={initializePreview}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-all duration-200 text-sm font-medium"
                title="Initialize MomentScience SDK with current settings. Check browser console for detailed logs."
              >
                <Play className="w-4 h-4" />
                <span>Test Live Preview</span>
              </button>
              
              <button
                onClick={resetToDefaults}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reset</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h1 className="text-4xl font-bold text-[#1A2B49] mb-4 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Customize Your MomentScience Experience
            </h1>
            <p className="text-lg text-[#6E6D7A] max-w-3xl mx-auto font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              Configure your Moments Offer Unit settings to create the perfect user experience for your website
            </p>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8">
                {[
                  { id: 'basic', label: 'Basic Settings', icon: Settings },
                  { id: 'appearance', label: 'Appearance', icon: Palette },
                  { id: 'behavior', label: 'Behavior', icon: Monitor },
                  { id: 'styles', label: 'Styles', icon: Layout }
                ].map((tab) => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`inline-flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'border-[#FE4215] text-[#FE4215]'
                          : 'border-transparent text-[#6E6D7A] hover:text-[#1A2B49] hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Settings Panel */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                
                {/* Basic Settings Tab */}
                {activeTab === 'basic' && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-[#1A2B49] mb-4">Basic Configuration</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <ToggleButton
                          isOn={config.autoShow}
                          onToggle={() => handleToggle('autoShow')}
                          label="Auto Show"
                        />
                        <p className="text-xs text-[#6E6D7A]">Automatically display offers when ready</p>
                        
                        <ToggleButton
                          isOn={config.autoLoad}
                          onToggle={() => handleToggle('autoLoad')}
                          label="Auto Load"
                        />
                        <p className="text-xs text-[#6E6D7A]">Automatically fetch offers when SDK is ready</p>
                        
                        <ToggleButton
                          isOn={config.dev}
                          onToggle={() => handleToggle('dev')}
                          label="Development Mode"
                        />
                        <p className="text-xs text-[#6E6D7A]">Enable testing mode for development</p>
                      </div>
                      
                      <div className="space-y-4">
                        <ToggleButton
                          isOn={config.multi_offer_unit}
                          onToggle={() => handleToggle('multi_offer_unit')}
                          label="Multi Offer Unit"
                        />
                        <p className="text-xs text-[#6E6D7A]">Display multiple offers simultaneously</p>
                        
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Display Delay (seconds)
                          </label>
                          <input
                            type="number"
                            value={config.delay}
                            onChange={(e) => handleInputChange('delay', parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                            min="0"
                            max="10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Appearance Tab */}
                {activeTab === 'appearance' && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-[#1A2B49] mb-4">Appearance Settings</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Position
                          </label>
                          <select
                            value={config.ad_position}
                            onChange={(e) => handleInputChange('ad_position', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                          >
                            {positions.map(pos => (
                              <option key={pos.value} value={pos.value}>{pos.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Animation
                          </label>
                          <select
                            value={config.ad_animation}
                            onChange={(e) => handleInputChange('ad_animation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                          >
                            {animations.map(anim => (
                              <option key={anim.value} value={anim.value}>{anim.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Screen Margin (px)
                          </label>
                          <input
                            type="number"
                            value={config.screen_margin}
                            onChange={(e) => handleInputChange('screen_margin', parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                            min="0"
                            max="100"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <ToggleButton
                          isOn={config.darken_bg}
                          onToggle={() => handleToggle('darken_bg')}
                          label="Darken Background (Center)"
                        />
                        <p className="text-xs text-[#6E6D7A]">Darken background when position is center</p>
                        
                        <ToggleButton
                          isOn={config.darken_bg_non_centered}
                          onToggle={() => handleToggle('darken_bg_non_centered')}
                          label="Darken Background (Non-Center)"
                        />
                        <p className="text-xs text-[#6E6D7A]">Darken background for non-center positions</p>
                        
                        <ToggleButton
                          isOn={config.show_close}
                          onToggle={() => handleToggle('show_close')}
                          label="Show Close Button"
                        />
                        <p className="text-xs text-[#6E6D7A]">Display the X close button</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Behavior Tab */}
                {activeTab === 'behavior' && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-[#1A2B49] mb-4">Behavior Settings</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <ToggleButton
                          isOn={config.close_ad_on_click_outside}
                          onToggle={() => handleToggle('close_ad_on_click_outside')}
                          label="Close on Outside Click"
                        />
                        <p className="text-xs text-[#6E6D7A]">Close offer when clicking outside</p>
                        
                        <ToggleButton
                          isOn={config.show_ads_on_exit}
                          onToggle={() => handleToggle('show_ads_on_exit')}
                          label="Show on Exit Intent"
                        />
                        <p className="text-xs text-[#6E6D7A]">Show offers when user intends to leave</p>
                        
                        <ToggleButton
                          isOn={config.enable_effect_shimmer_pos_cta}
                          onToggle={() => handleToggle('enable_effect_shimmer_pos_cta')}
                          label="Shimmer Effect"
                        />
                        <p className="text-xs text-[#6E6D7A]">Add shimmer effect to positive CTA</p>
                      </div>
                      
                      <div className="space-y-4">
                        <ToggleButton
                          isOn={config.enable_close_delay}
                          onToggle={() => handleToggle('enable_close_delay')}
                          label="Enable Close Delay"
                        />
                        <p className="text-xs text-[#6E6D7A]">Delay showing the close button</p>
                        
                        {config.enable_close_delay && (
                          <div>
                            <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                              Close Delay (seconds)
                            </label>
                            <input
                              type="number"
                              value={config.close_delay}
                              onChange={(e) => handleInputChange('close_delay', parseInt(e.target.value) || 1)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                              min="1"
                              max="10"
                            />
                          </div>
                        )}
                        
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Button Order
                          </label>
                          <select
                            value={config.button_order}
                            onChange={(e) => handleInputChange('button_order', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                          >
                            <option value="pos_first">Positive First</option>
                            <option value="neg_first">Negative First</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Embedded Settings */}
                    <div className="border-t pt-6">
                      <h4 className="text-lg font-semibold text-[#1A2B49] mb-4">Embedded Unit Settings</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <ToggleButton
                            isOn={config.embedded.enabled}
                            onToggle={() => handleToggle('enabled', 'embedded')}
                            label="Enable Embedded Mode"
                          />
                          <p className="text-xs text-[#6E6D7A]">Embed unit into page instead of modal</p>
                          
                          {config.embedded.enabled && (
                            <>
                              <ToggleButton
                                isOn={config.embedded.showBorder}
                                onToggle={() => handleToggle('showBorder', 'embedded')}
                                label="Show Border"
                              />
                              
                              <ToggleButton
                                isOn={config.embedded.showHeader}
                                onToggle={() => handleToggle('showHeader', 'embedded')}
                                label="Show Header"
                              />
                            </>
                          )}
                        </div>
                        
                        {config.embedded.enabled && (
                          <div className="space-y-4">
                            <ToggleButton
                              isOn={config.embedded.showFooter}
                              onToggle={() => handleToggle('showFooter', 'embedded')}
                              label="Show Footer"
                            />
                            
                            <ToggleButton
                              isOn={config.embedded.fill_container}
                              onToggle={() => handleToggle('fill_container', 'embedded')}
                              label="Fill Container"
                            />
                            
                            <div>
                              <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                                Target Element ID
                              </label>
                              <input
                                type="text"
                                value={config.embedded.targetElement}
                                onChange={(e) => handleInputChange('targetElement', e.target.value, 'embedded')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                                placeholder="adpx"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Styles Tab */}
                {activeTab === 'styles' && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-[#1A2B49] mb-4">Style Customization</h3>
                    
                    {/* Header Styles */}
                    <div className="border rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-[#1A2B49] mb-4">Header Styles</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Background Color
                          </label>
                          <input
                            type="color"
                            value={config.styles.header.background}
                            onChange={(e) => handleStyleChange('header', 'background', e.target.value)}
                            className="w-full h-10 border border-gray-300 rounded-md"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Text Color
                          </label>
                          <input
                            type="color"
                            value={config.styles.header.textColor}
                            onChange={(e) => handleStyleChange('header', 'textColor', e.target.value)}
                            className="w-full h-10 border border-gray-300 rounded-md"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Header Text
                          </label>
                          <input
                            type="text"
                            value={config.styles.header.text}
                            onChange={(e) => handleStyleChange('header', 'text', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-[#1A2B49] mb-2">
                            Lead-in Text
                          </label>
                          <input
                            type="text"
                            value={config.styles.header.lead_in_text}
                            onChange={(e) => handleStyleChange('header', 'lead_in_text', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Button Styles */}
                    <div className="border rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-[#1A2B49] mb-4">Button Styles</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h5 className="font-medium text-[#1A2B49] mb-3">Positive Button</h5>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-[#6E6D7A] mb-1">Background</label>
                              <input
                                type="color"
                                value={config.styles.offerText.buttonYes.background}
                                onChange={(e) => setConfig(prev => ({
                                  ...prev,
                                  styles: {
                                    ...prev.styles,
                                    offerText: {
                                      ...prev.styles.offerText,
                                      buttonYes: {
                                        ...prev.styles.offerText.buttonYes,
                                        background: e.target.value
                                      }
                                    }
                                  }
                                }))}
                                className="w-full h-8 border border-gray-300 rounded-md"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-[#6E6D7A] mb-1">Text Color</label>
                              <input
                                type="color"
                                value={config.styles.offerText.buttonYes.color}
                                onChange={(e) => setConfig(prev => ({
                                  ...prev,
                                  styles: {
                                    ...prev.styles,
                                    offerText: {
                                      ...prev.styles.offerText,
                                      buttonYes: {
                                        ...prev.styles.offerText.buttonYes,
                                        color: e.target.value
                                      }
                                    }
                                  }
                                }))}
                                className="w-full h-8 border border-gray-300 rounded-md"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-[#1A2B49] mb-3">Negative Button</h5>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-[#6E6D7A] mb-1">Background</label>
                              <input
                                type="color"
                                value={config.styles.offerText.buttonNo.background}
                                onChange={(e) => setConfig(prev => ({
                                  ...prev,
                                  styles: {
                                    ...prev.styles,
                                    offerText: {
                                      ...prev.styles.offerText,
                                      buttonNo: {
                                        ...prev.styles.offerText.buttonNo,
                                        background: e.target.value
                                      }
                                    }
                                  }
                                }))}
                                className="w-full h-8 border border-gray-300 rounded-md"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-[#6E6D7A] mb-1">Text Color</label>
                              <input
                                type="color"
                                value={config.styles.offerText.buttonNo.color}
                                onChange={(e) => setConfig(prev => ({
                                  ...prev,
                                  styles: {
                                    ...prev.styles,
                                    offerText: {
                                      ...prev.styles.offerText,
                                      buttonNo: {
                                        ...prev.styles.offerText.buttonNo,
                                        color: e.target.value
                                      }
                                    }
                                  }
                                }))}
                                className="w-full h-8 border border-gray-300 rounded-md"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Preview & Code Panel */}
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Debug Status Panel */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Debug Status</h4>
                  <button
                    onClick={() => {
                      console.log('🔍 Current window.AdpxConfig:', (window as any).AdpxConfig)
                      console.log('🔍 Current window.AdpxUser:', (window as any).AdpxUser)
                      console.log('🔍 SDK Available:', !!(window as any).Adpx)
                      if ((window as any).Adpx) {
                        console.log('🔍 SDK Methods:', Object.keys((window as any).Adpx))
                      }
                    }}
                    className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                  >
                    Log Debug Info
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-gray-600">SDK Loaded:</span>
                    <span className={`ml-2 px-2 py-1 rounded ${
                      typeof window !== 'undefined' && (window as any).Adpx 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {typeof window !== 'undefined' && (window as any).Adpx ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Auto Show:</span>
                    <span className={`ml-2 px-2 py-1 rounded ${
                      config.autoShow ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {config.autoShow ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Dev Mode:</span>
                    <span className={`ml-2 px-2 py-1 rounded ${
                      config.dev ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {config.dev ? 'On' : 'Off'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Embedded:</span>
                    <span className={`ml-2 px-2 py-1 rounded ${
                      config.embedded.enabled ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {config.embedded.enabled ? 'On' : 'Off'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Position:</span>
                    <span className="ml-2 text-gray-800">{config.embedded.enabled ? 'Embedded' : config.ad_position}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Target:</span>
                    <span className="ml-2 text-gray-800">#{config.embedded.targetElement}</span>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                  <strong>💡 No offers showing?</strong> Check: Account setup, targeting rules, geographic restrictions, or theme configuration.
                </div>
              </div>

              {/* Generated Code */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#1A2B49]">Generated Code</h3>
                  <button
                    onClick={() => setShowCode(!showCode)}
                    className="text-[#FE4215] hover:text-[#E53910] transition-colors"
                  >
                    {showCode ? 'Hide' : 'Show'} Code
                  </button>
                </div>
                
                {showCode && (
                  <div className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
                    <pre>{generateCode()}</pre>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center space-x-2 text-green-800">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">Auto-save enabled - All changes saved automatically</span>
                  </div>
                </div>
                
                <Link
                  to="/product-selection"
                  className="w-full inline-flex items-center justify-center space-x-2 px-4 py-3 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-colors font-medium"
                >
                  <Eye className="w-4 h-4" />
                  <span>Test in Real Scenario</span>
                </Link>
                
                <button 
                  onClick={() => window.location.reload()}
                  className="w-full inline-flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  <Settings className="w-4 h-4" />
                  <span>Reset to Defaults</span>
                </button>
              </div>
            </motion.div>
          </div>

          {/* Full-Width Embedded Preview Section */}
          {config.embedded.enabled && (
            <motion.div variants={itemVariants} className="mt-8">
              <div className="bg-white rounded-lg p-8 shadow-sm border-l-4 border-[#FE4215]">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-semibold text-[#1A2B49] mb-2">Live Embedded Preview</h3>
                    <p className="text-sm text-[#6E6D7A]">
                      Target Element: <code className="bg-gray-100 px-2 py-1 rounded text-xs">#{config.embedded.targetElement}</code>
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-[#6E6D7A] mb-1">Embedded Mode</div>
                    <div className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium">
                      ACTIVE
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border-2 border-dashed border-gray-300">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center space-x-2 text-sm text-gray-600 mb-4">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span>Embedded container ready for Moments</span>
                    </div>
                  </div>
                  
                  <div 
                    id={config.embedded.targetElement}
                    className={`
                      mx-auto rounded-lg transition-all duration-300 relative
                      ${config.embedded.showBorder ? 'border-2 border-[#FE4215]/30' : 'border-0'}
                      ${config.embedded.fill_container ? 'w-full' : 'max-w-2xl'}
                      bg-white shadow-sm flex items-center justify-center text-gray-500
                      hover:shadow-md transition-shadow duration-200
                    `}
                    style={{
                      minHeight: config.embedded.fill_container ? '450px' : '350px',
                      maxWidth: config.embedded.fill_container ? '100%' : '800px'
                    }}
                  >
                    <div className="text-center p-10">
                      <div className="w-20 h-20 bg-gradient-to-br from-[#FE4215] to-[#233D71] rounded-full flex items-center justify-center mx-auto mb-6">
                        <Settings className="w-10 h-10 text-white" />
                      </div>
                      <div className="text-xl font-medium text-[#1A2B49] mb-3">Embedded Moment Container</div>
                      <div className="text-sm text-[#6E6D7A] mb-6">
                        {config.embedded.showHeader && config.embedded.showFooter && 'Header & Footer Enabled'}
                        {config.embedded.showHeader && !config.embedded.showFooter && 'Header Enabled'}
                        {!config.embedded.showHeader && config.embedded.showFooter && 'Footer Enabled'}
                        {!config.embedded.showHeader && !config.embedded.showFooter && 'Header & Footer Disabled'}
                      </div>
                      <div className="inline-flex items-center space-x-2 text-sm text-gray-400 bg-gray-50 px-4 py-2 rounded-full">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span>Click "Test Live Preview" to load offers</span>
                      </div>
                      
                      {/* Settings indicators */}
                      <div className="flex items-center justify-center space-x-6 mt-6 pt-6 border-t border-gray-100">
                        <div className="text-sm text-center">
                          <div className="font-medium text-gray-600 mb-1">Border</div>
                          <div className={`text-sm ${config.embedded.showBorder ? 'text-green-600' : 'text-gray-400'}`}>
                            {config.embedded.showBorder ? 'ON' : 'OFF'}
                          </div>
                        </div>
                        <div className="text-sm text-center">
                          <div className="font-medium text-gray-600 mb-1">Fill Container</div>
                          <div className={`text-sm ${config.embedded.fill_container ? 'text-green-600' : 'text-gray-400'}`}>
                            {config.embedded.fill_container ? 'ON' : 'OFF'}
                          </div>
                        </div>
                        <div className="text-sm text-center">
                          <div className="font-medium text-gray-600 mb-1">Header/Footer</div>
                          <div className="text-sm text-gray-600">
                            {config.embedded.showHeader && config.embedded.showFooter && 'Both'}
                            {config.embedded.showHeader && !config.embedded.showFooter && 'Header Only'}
                            {!config.embedded.showHeader && config.embedded.showFooter && 'Footer Only'}
                            {!config.embedded.showHeader && !config.embedded.showFooter && 'None'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">i</span>
                    </div>
                    <div className="text-sm text-blue-800">
                      <strong>Embedded Mode:</strong> Offers will render inside this container instead of as popup overlays. 
                      Perfect for integrating Moments seamlessly into your page layout. The container will automatically 
                      adjust its size and appearance based on your configuration settings.
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default MomentCustomizationPage
